/**
 * OTPVerifyScreen — 6-digit OTP entry for contact portal access.
 *
 * Features:
 *   - 6 individual digit inputs with auto-advance
 *   - Paste support (pastes a 6-digit code across all fields)
 *   - Resend button with 60-second cooldown
 *   - Error messaging from API
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { auth } from '../../lib/api.js';
import { useAuth } from '../../lib/auth-context.js';

interface Props {
  magicToken: string;
  maskedPhone?: string | undefined;
  /** Called with { entityType, entityId } on successful verification */
  onSuccess: (entityType: string, entityId: string) => void;
}

const RESEND_COOLDOWN = 60;

// Tracks tokens that have already triggered an OTP request.
// Module-level so it survives React Strict Mode's double-mount.
const otpTriggeredTokens = new Set<string>();

export default function OTPVerifyScreen({ magicToken, maskedPhone, onSuccess }: Props): React.ReactElement {
  const { loginWithOTP } = useAuth();
  const [digits,      setDigits]     = useState<string[]>(Array(6).fill(''));
  const [error,       setError]      = useState<string | null>(null);
  const [loading,     setLoading]    = useState(false);
  const [resendSecs,  setResendSecs] = useState(0);
  const [focusedIdx,  setFocusedIdx] = useState<number | null>(null);
  const [maskedLocal, setMaskedLocal] = useState<string | undefined>(maskedPhone);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-start the OTP request on mount.
  // Module-level set prevents double-fire from React Strict Mode.
  useEffect(() => {
    if (otpTriggeredTokens.has(magicToken)) return;
    otpTriggeredTokens.add(magicToken);
    void triggerOTP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicToken]);

  // Countdown timer
  useEffect(() => {
    if (resendSecs <= 0) return;
    const t = setTimeout(() => setResendSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSecs]);

  const triggerOTP = async () => {
    setResendSecs(RESEND_COOLDOWN);
    setError(null);
    try {
      const res = await auth.requestOTP(magicToken);
      if (res.success) {
        if (res.data?.contactMasked) setMaskedLocal(res.data.contactMasked);
      } else {
        setError(res.error ?? 'Failed to send verification code. Please try again.');
        setResendSecs(0);
      }
    } catch {
      setError('Failed to send verification code. Please try again.');
      setResendSecs(0);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 digits are filled
    if (digit && next.every((d) => d !== '')) {
      void submitOTP(next.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) {
      next[i] = pasted[i] ?? '';
    }
    setDigits(next);
    // Focus last filled input
    const lastIndex = Math.min(pasted.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
    if (pasted.length === 6) {
      void submitOTP(pasted);
    }
  };

  const submitOTP = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      const { entityType, entityId } = await loginWithOTP(magicToken, code);
      onSuccess(entityType, entityId);
    } catch (err) {
      setError((err as Error).message || 'Invalid code. Please try again.');
      setDigits(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [magicToken, onSuccess, loginWithOTP]);

  const handleSubmit = () => {
    const code = digits.join('');
    if (code.length !== 6) { setError('Please enter all 6 digits'); return; }
    void submitOTP(code);
  };

  // SVG lock icon — no emoji
  const LockIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );

  return (
    <div className="ci-portal" style={styles.container}>
      <div className="ci-enter" style={styles.card}>
        {/* Lock mark */}
        <div style={styles.iconWrap}>
          <LockIcon />
        </div>

        <h1 style={styles.title}>Verify Your Identity</h1>
        <p style={styles.subtitle}>
          We sent a 6-digit code to your{' '}
          {maskedLocal ? <strong style={{ color: '#1a2744' }}>{maskedLocal}</strong> : 'phone number on file'}.
          Enter it below to continue.
        </p>

        {/* OTP inputs — staggered entrance */}
        <div style={styles.digitRow} onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              className={`ci-digit-${i}`}
              style={{
                ...styles.digitInput,
                ...(loading ? styles.digitDisabled : {}),
                ...(digit ? styles.digitFilled : {}),
                ...(focusedIdx === i ? styles.digitFocused : {}),
              }}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onFocus={() => setFocusedIdx(i)}
              onBlur={() => setFocusedIdx(null)}
              disabled={loading}
              aria-label={`Digit ${i + 1} of 6`}
            />
          ))}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button
          style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Verifying…' : 'Continue'}
        </button>

        <div style={styles.resendRow}>
          {resendSecs > 0 ? (
            <span style={styles.resendWait}>Resend in {resendSecs}s</span>
          ) : (
            <button style={styles.resendBtn} onClick={() => void triggerOTP()}>
              Send a new code
            </button>
          )}
        </div>

        <div style={styles.secureNote}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9e9eaa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          End-to-end encrypted
        </div>
      </div>
    </div>
  );
}

const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     'var(--ci-surface)',
    padding:        '24px 16px',
    fontFamily:     F,
  },
  card: {
    background:   '#ffffff',
    borderRadius: 16,
    padding:      '44px 36px',
    maxWidth:     420,
    width:        '100%',
    border:       '1px solid var(--ci-border)',
    textAlign:    'center',
    fontFamily:   F,
  },
  iconWrap: {
    width:          56, height: 56,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    margin:         '0 auto 24px',
  },
  title:    { margin: '0 0 10px', fontSize: 22, fontWeight: 700, color: 'var(--ci-text)', fontFamily: F },
  subtitle: { margin: '0 0 28px', fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.65, fontFamily: F },
  digitRow: {
    display:        'flex',
    justifyContent: 'center',
    gap:            10,
    marginBottom:   20,
  },
  digitInput: {
    width:        46, height: 54,
    border:       '2px solid var(--ci-border)',
    borderRadius: 10,
    fontSize:     22,
    fontWeight:   700,
    textAlign:    'center',
    color:        'var(--ci-text)',
    outline:      'none',
    transition:   'border-color 0.15s, background 0.15s',
    fontFamily:   F,
  },
  digitFilled: {
    borderColor: 'var(--ci-primary)',
    background:  'var(--ci-primary-light)',
  },
  digitFocused: {
    borderColor: 'var(--ci-primary)',
    boxShadow:   '0 0 0 3px var(--ci-primary-focus)',
    outline:     'none',
  },
  digitDisabled: {
    background: 'var(--ci-border-light)',
    color:      'var(--ci-text-muted)',
  },
  error: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 16px',
    color:        '#c0392b',
    fontSize:     14,
    marginBottom: 16,
    textAlign:    'left',
    fontFamily:   F,
  },
  submitBtn: {
    width:        '100%',
    padding:      '13px',
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    fontSize:     15,
    fontWeight:   600,
    cursor:       'pointer',
    marginBottom: 16,
    fontFamily:   F,
    transition:   'background 0.15s, transform 0.1s',
    letterSpacing: '0.01em',
  },
  resendRow:  { marginBottom: 28 },
  resendWait: { fontSize: 13, color: 'var(--ci-text-muted)', fontFamily: F },
  resendBtn: {
    background:     'none',
    border:         'none',
    color:          'var(--ci-primary)',
    cursor:         'pointer',
    fontSize:       13,
    fontWeight:     500,
    textDecoration: 'underline',
    fontFamily:     F,
  },
  secureNote: {
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap:        5,
    fontSize:   11,
    color:      'var(--ci-text-muted)',
    lineHeight: 1.5,
    fontFamily: F,
  },
};
